import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'

export type WorkspaceMember =
  Database['public']['Tables']['workspace_members']['Row']

export interface WorkspaceMemberWithProfile extends WorkspaceMember {
  profiles: {
    email: string
    full_name: string | null
    avatar_url: string | null
  }
}

async function addWorkspaceMember(workspaceId: string, userId: string) {
  const { data, error } = await supabase
    .from('workspace_members')
    .insert({ workspace_id: workspaceId, user_id: userId })
    .select('*, profiles!inner(email, full_name, avatar_url)')
    .single()

  if (error) {
    throw new Error(`Failed to add workspace member: ${error.message}`, {
      cause: error,
    })
  }

  return data as unknown as WorkspaceMemberWithProfile
}

async function removeWorkspaceMember(workspaceId: string, userId: string) {
  const { error } = await supabase
    .from('workspace_members')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)

  if (error) {
    throw new Error(`Failed to remove workspace member: ${error.message}`, {
      cause: error,
    })
  }
}

export const workspaceMember = {
  findManyByWorkspace: (workspaceId: string) =>
    queryOptions({
      queryKey: ['workspace-members', 'workspace', workspaceId],
      queryFn: async (): Promise<WorkspaceMemberWithProfile[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch members without valid workspace ID'
          )
        }

        const { data, error } = await supabase
          .from('workspace_members')
          .select('*, profiles!inner(email, full_name, avatar_url)')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch workspace members: ${error.message}`,
            { cause: error }
          )
        }

        return (data || []) as unknown as WorkspaceMemberWithProfile[]
      },
      staleTime: 5 * 60 * 1000,
    }),

  isMember: (workspaceId: string, userId: string) =>
    queryOptions({
      queryKey: ['workspace-members', 'workspace', workspaceId, 'user', userId],
      queryFn: async (): Promise<boolean> => {
        if (!workspaceId || !userId) {
          return false
        }

        const { data, error } = await supabase
          .from('workspace_members')
          .select('workspace_id')
          .eq('workspace_id', workspaceId)
          .eq('user_id', userId)
          .maybeSingle()

        if (error) {
          throw new Error(
            `Failed to check workspace membership: ${error.message}`,
            { cause: error }
          )
        }

        return data !== null
      },
      staleTime: 5 * 60 * 1000,
    }),

  add: addWorkspaceMember,
  remove: removeWorkspaceMember,
}
